import i18n from "@/Plugins/i18n";

import type { EventFilter } from "@/Modules/Chat/WebSocketManager";
import type { VideoCallOptions } from "@/Modules/VideoCall/Models/State";
import { config } from "@/Common/envConfig";
import {
  MessageTypeWebSocket,
  type VideoCallRoomCreatedMessageWebSocket,
} from "@/Modules/Chat/Models/WebSocket";
import { VIDEO_CALL_EXCEPTIONS } from "@/Modules/VideoCall/Components/definitions";
import { VIDEO_CALL_COMPLETE_REASON } from "@/Modules/Models/Api/VideoCalls";

import { type VideoCallStore, useVideoCallStore } from "@/Modules/VideoCall/Store";
import { type UserStore, useUserStore } from "@/Modules/User/Store";
import { type ChatStore, useChatStore } from "../Store";
import useAppStore, { type AppStore } from "@/Modules/App/Store";

import { isObject } from "@/Utils/Objects";
import { isValidString } from "@/Utils/String";
import { not } from "@/Utils/Promises";

import { hasPermissions, hasSupport } from "@/Modules/VideoCall/Utils";
import { log } from "@/Utils/Debug";

export interface ExtraParamsCreateVideoCall {
  videoCallStore: VideoCallStore;
  userStore: UserStore;
  chatStore: ChatStore;
  appStore: AppStore;
}

export const isVideoCallCreatedMessage = (
  message: unknown
): message is VideoCallRoomCreatedMessageWebSocket => {
  return (
    isObject(message) &&
    message.type === MessageTypeWebSocket.VIDEO_CALL_ROOM_CREATED &&
    isObject(message.data) &&
    isValidString(message.data.room_token)
  );
};

export const videoCallCreatedHandler = async (
  createVideoCallMessageWebSocket: VideoCallRoomCreatedMessageWebSocket,
  {
    videoCallStore = useVideoCallStore(),
    userStore = useUserStore(),
    chatStore = useChatStore(),
    appStore = useAppStore(),
  }: ExtraParamsCreateVideoCall
): Promise<undefined> => {
  if (isValidString(videoCallStore.token)) return;
  if (userStore.getUserIsTherapist) return;

  const [chatId] = Object.keys(chatStore.rooms);

  if (userStore.getUserIsPatient && isValidString(chatId)) {
    videoCallStore.clean();

    if (!hasSupport() || (await not(hasPermissions()))) {
      log("NOT PERMISSIONS");

      videoCallStore.updateVideoCallError(true);
      videoCallStore.addError(VIDEO_CALL_EXCEPTIONS.NOT_PERMISSIONS);

      await videoCallStore.sendVideoCallLogs(createVideoCallMessageWebSocket.data.room_uuid);
      await videoCallStore.hangDownVideoCall(
        createVideoCallMessageWebSocket.data.room_uuid,
        VIDEO_CALL_COMPLETE_REASON.PERMISSIONS
      );

      return undefined;
    }

    const room = chatStore.rooms[chatId]!;
    const user = room.user;
    const videoCallConfig: VideoCallOptions = {
      sessionId: createVideoCallMessageWebSocket.data.room_sid,
      token: createVideoCallMessageWebSocket.data.room_token,
      roomId: createVideoCallMessageWebSocket.data.room_uuid,
      isTranscribed: createVideoCallMessageWebSocket.data.is_transcription_enabled,
      remoteUser: user,
      isRatingRequired: createVideoCallMessageWebSocket.data.is_rating_required,
      sessionDuration: room.sessionDuration ?? config.defaultMinSessionDuration,
    };

    videoCallStore.disableRating();

    if (await videoCallStore.isValidConfig()) {
      videoCallStore.showVideoCallError = false;

      // Tener en cuenta que si el paciente en algún momento tiene mas de un chat esto se verá modificado

      chatStore.setActiveChatRoom(chatId);
      videoCallStore.setupVideoCall(videoCallConfig);

      appStore.notificate({
        title: user.username,
        body: i18n.global.t("browser-notifications.new_videocall"),
      });
    } else {
      chatStore.setActiveChatRoom(chatId);

      videoCallStore.updateShowConfig(true);
      videoCallStore.updateNextCallOptions(videoCallConfig);
    }
  }

  return Promise.resolve(undefined);
};

export default {
  isType: isVideoCallCreatedMessage,
  handler: videoCallCreatedHandler,
} as EventFilter<ExtraParamsCreateVideoCall>;
